/**
 * 样式继承解析器
 * 处理 ParagraphStyle 的 basedOn 继承链和 NumberingLevel 的样式合并
 */

import type {
  ParagraphStyle,
  NumberingLevel,
  ComputedLevelStyle,
  SchemaInput,
  Font,
  Spacing,
} from './types';

// ============ 工具函数 ============

/**
 * 过滤对象中的 null/undefined 值
 */
function filterNonNull<T extends object>(obj: T): Partial<T> {
  const result: Partial<T> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value != null) {
      (result as Record<string, unknown>)[key] = value;
    }
  }
  return result;
}

// ============ 样式链解析 ============

/**
 * 解析段落样式的继承链
 * @param styleId - 样式 ID
 * @param styles - 所有段落样式
 * @returns 按继承顺序排列的样式数组（基类在前）
 */
export function resolveStyleChain(
  styleId: string | undefined | null,
  styles: Record<string, ParagraphStyle>
): ParagraphStyle[] {
  const chain: ParagraphStyle[] = [];
  const visited = new Set<string>();

  let currentId = styleId;

  while (currentId && styles[currentId] && !visited.has(currentId)) {
    visited.add(currentId);
    chain.unshift(styles[currentId]); // 基类在前
    currentId = styles[currentId].basedOn;
  }

  return chain;
}

/**
 * 合并样式继承链得到最终样式
 * @param styleId - 样式 ID
 * @param styles - 所有段落样式
 * @returns 合并后的段落样式
 */
export function resolveMergedStyle(
  styleId: string | undefined | null,
  styles: Record<string, ParagraphStyle>
): ParagraphStyle {
  const chain = resolveStyleChain(styleId, styles);

  if (chain.length === 0) {
    return { name: '' };
  }

  let merged: ParagraphStyle = { name: '' };

  for (const style of chain) {
    // 合并 spacing
    if (style.spacing) {
      merged.spacing = {
        ...merged.spacing,
        ...filterNonNull(style.spacing),
      } as Spacing;
    }

    // 合并 font
    if (style.font) {
      merged.font = {
        ...merged.font,
        ...filterNonNull(style.font),
      } as Font;
    }

    // 合并其他属性
    if (style.fontSize !== undefined) merged.fontSize = style.fontSize;
    if (style.bold !== undefined) merged.bold = style.bold;
    if (style.italic !== undefined) merged.italic = style.italic;
    if (style.underline !== undefined) merged.underline = style.underline;
    if (style.indent !== undefined) {
      merged.indent = { ...merged.indent, ...filterNonNull(style.indent) };
    }

    merged.name = style.name;
  }

  return merged;
}

// ============ Level 样式计算 ============

/**
 * 计算 NumberingLevel 的完整样式（合并关联的 ParagraphStyle）
 * @param level - 编号级别定义
 * @param styles - 所有段落样式
 * @returns 计算后的完整样式
 */
export function computeLevelStyle(
  level: NumberingLevel,
  styles: Record<string, ParagraphStyle>
): ComputedLevelStyle {
  // 基础属性来自 level 定义
  const computed: ComputedLevelStyle = {
    level: level.level,
    start: level.start,
    numFmt: level.numFmt,
    pattern: level.pattern,
    indent: { ...level.indent },
  };

  // 如果有关联样式，合并样式属性
  if (level.style) {
    const mergedStyle = resolveMergedStyle(level.style, styles);

    if (mergedStyle.spacing) {
      computed.spacing = mergedStyle.spacing;
    }
    if (mergedStyle.font) {
      computed.font = mergedStyle.font;
    }
    if (mergedStyle.fontSize !== undefined) {
      computed.fontSize = mergedStyle.fontSize;
    }
    if (mergedStyle.bold !== undefined) {
      computed.bold = mergedStyle.bold;
    }
    if (mergedStyle.italic !== undefined) {
      computed.italic = mergedStyle.italic;
    }
    if (mergedStyle.underline !== undefined) {
      computed.underline = mergedStyle.underline;
    }
  }

  return computed;
}

/**
 * 获取指定 AbstractNum 的所有级别的计算样式
 * @param schema - 完整 Schema
 * @param abstractNumId - AbstractNum ID
 * @param maxLevel - 最大级别（默认 4，即 Level 0-4）
 * @returns 计算后的级别样式数组
 */
export function computeAllLevelStyles(
  schema: SchemaInput,
  abstractNumId: number,
  maxLevel = 4
): ComputedLevelStyle[] {
  const abstractKey = `AbstractNum_${abstractNumId}`;
  const abstractNum = schema.numberingSchemas[abstractKey];

  if (!abstractNum) {
    throw new Error(`AbstractNum_${abstractNumId} not found in schema`);
  }

  const results: ComputedLevelStyle[] = [];

  for (let i = 0; i <= Math.min(maxLevel, abstractNum.levels.length - 1); i++) {
    results.push(computeLevelStyle(abstractNum.levels[i], schema.paragraphStyles));
  }

  return results;
}

// ============ 样式查找辅助 ============

/**
 * 获取 Level 关联的样式 ID
 */
export function getLevelStyleId(
  schema: SchemaInput,
  abstractNumId: number,
  level: number
): string | undefined {
  const abstractKey = `AbstractNum_${abstractNumId}`;
  const abstractNum = schema.numberingSchemas[abstractKey];

  if (!abstractNum || !abstractNum.levels[level]) {
    return undefined;
  }

  return abstractNum.levels[level].style;
}

/**
 * 检查样式是否存在
 */
export function styleExists(
  schema: SchemaInput,
  styleId: string
): boolean {
  return styleId in schema.paragraphStyles;
}

/**
 * 获取所有被 Numbering 引用的样式 ID
 */
export function getReferencedStyleIds(schema: SchemaInput): Set<string> {
  const styleIds = new Set<string>();

  for (const abstractNum of Object.values(schema.numberingSchemas)) {
    for (const level of abstractNum.levels) {
      if (level.style) {
        styleIds.add(level.style);
      }
    }
  }

  return styleIds;
}
